import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../utils/api';

function fmtBytes(n) {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
const fmtDT = d => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtD = d => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

function Stat({ label, value, sub }) {
  return (
    <div className="card" style={{ padding: '14px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function ProofImagesPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('images');
  const [clientId, setClientId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState([]);
  const [binSelected, setBinSelected] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const h = e => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [lightbox]);

  const { data, isLoading } = useQuery({
    queryKey: ['proofs', clientId, from, to],
    queryFn: () => api.get('/proofs', { params: { clientId: clientId || undefined, from: from || undefined, to: to || undefined } }).then(r => r.data),
  });
  const { data: bin } = useQuery({ queryKey: ['proofs-trash'], queryFn: () => api.get('/proofs/trash').then(r => r.data) });
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: () => api.get('/clients').then(r => r.data) });

  const items = data?.items || [];
  const refresh = () => { qc.invalidateQueries(['proofs']); qc.invalidateQueries(['proofs-trash']); };

  const toBin = useMutation({
    mutationFn: ids => api.delete('/proofs', { data: { entry_ids: ids } }),
    onSuccess: r => { toast.success(`${r.data.moved} image(s) moved to the Recycle Bin (${fmtBytes(r.data.bytes)})`); setSelected([]); refresh(); },
    onError: () => toast.error('Failed to delete'),
  });
  const restore = useMutation({
    mutationFn: ids => api.post('/proofs/trash/restore', { ids }),
    onSuccess: r => {
      toast.success(`${r.data.restored} image(s) restored`);
      (r.data.skipped || []).forEach(m => toast.error(`Not restored — ${m}`, { duration: 7000 }));
      setBinSelected([]); refresh();
    },
    onError: () => toast.error('Failed to restore'),
  });
  const purge = useMutation({
    mutationFn: body => api.delete('/proofs/trash', { data: body }),
    onSuccess: r => { toast.success(`${r.data.deleted} image(s) permanently deleted — ${fmtBytes(r.data.bytes)} freed`); setBinSelected([]); refresh(); },
    onError: () => toast.error('Failed to delete'),
  });

  const allSelected = items.length > 0 && items.every(i => selected.includes(i.entry_id));
  const toggle = id => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectedItems = items.filter(i => selected.includes(i.entry_id));
  const selectedBytes = selectedItems.reduce((s, i) => s + (i.size_bytes || 0), 0);
  const usedOnInvoices = selectedItems.filter(i => i.invoice_number).length;

  function confirmDelete() {
    if (!selected.length) return;
    const warn = usedOnInvoices ? `\n\n${usedOnInvoices} of these are used on an invoice — their reference thumbnails will disappear from the invoice editor.` : '';
    if (window.confirm(`Move ${selected.length} image(s) (${fmtBytes(selectedBytes)}) to the Recycle Bin?\n\nThey stay there for ${bin?.retention_days || 7} days and can be restored, then are deleted permanently.${warn}`)) {
      toBin.mutate(selected);
    }
  }

  const binItems = bin?.items || [];
  const allBinSelected = binItems.length > 0 && binItems.every(i => binSelected.includes(i.id));

  const disk = data?.server?.disk;
  const tabBtn = (id, label) => (
    <button onClick={() => setTab(id)} style={{ padding: '7px 16px', borderRadius: 6, fontSize: 13, border: '1px solid var(--border)', cursor: 'pointer',
      fontWeight: tab === id ? 700 : 400, background: tab === id ? 'var(--accent)' : 'var(--surface)', color: tab === id ? '#fff' : 'var(--text2)' }}>{label}</button>
  );

  return (
    <div>
      <div className="section-head">
        <h2>Proof Images</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {tabBtn('images', `Images${data ? ` (${data.summary.count})` : ''}`)}
          {tabBtn('bin', `🗑 Recycle Bin${binItems.length ? ` (${binItems.length})` : ''}`)}
        </div>
      </div>

      {tab === 'images' && <>
        <div className="form-grid" style={{ marginBottom: 14 }}>
          <Stat label="Images (current filter)" value={data?.summary.count ?? '…'} sub={`${fmtBytes(data?.summary.bytes)} used`} />
          <Stat label="All proof images on server" value={fmtBytes(data?.server.proofs.bytes)} sub={`${data?.server.proofs.files ?? 0} files`} />
          <Stat label="In Recycle Bin" value={fmtBytes(data?.server.trash.bytes)} sub={`${data?.server.trash.files ?? 0} files · auto-deleted after ${bin?.retention_days || 7} days`} />
          <Stat label="Server disk free" value={disk ? fmtBytes(disk.free_bytes) : '—'} sub={disk ? `of ${fmtBytes(disk.total_bytes)} total` : ''} />
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
          <div className="field" style={{ margin: 0, minWidth: 220 }}>
            <label>Client</label>
            <select value={clientId} onChange={e => { setClientId(e.target.value); setSelected([]); }}>
              <option value="">All clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field" style={{ margin: 0 }}><label>Uploaded from</label><input type="date" value={from} onChange={e => { setFrom(e.target.value); setSelected([]); }} /></div>
          <div className="field" style={{ margin: 0 }}><label>Uploaded to</label><input type="date" value={to} onChange={e => { setTo(e.target.value); setSelected([]); }} /></div>
          {(clientId || from || to) && <button className="btn-ghost btn-sm" onClick={() => { setClientId(''); setFrom(''); setTo(''); setSelected([]); }}>Clear filters</button>}
        </div>

        {!clientId && data?.summary.by_client.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {[...data.summary.by_client].sort((a, b) => b.bytes - a.bytes).map(c => (
              <button key={c.client_id} onClick={() => setClientId(c.client_id)} title="Filter to this client"
                style={{ padding: '4px 10px', fontSize: 12, borderRadius: 20, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer' }}>
                {c.client_name} · <strong>{c.count}</strong> · {fmtBytes(c.bytes)}
              </button>
            ))}
          </div>
        )}

        {selected.length > 0 && (
          <div style={{ background: 'var(--accent)', color: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <strong>{selected.length} selected</strong><span style={{ fontSize: 12 }}>{fmtBytes(selectedBytes)}</span>
            <div style={{ flex: 1 }} />
            <button className="btn-sm" onClick={() => setSelected([])}>Clear</button>
            <button className="btn-sm" style={{ background: '#fff', color: '#B91C1C', fontWeight: 700 }} disabled={toBin.isPending} onClick={confirmDelete}>🗑 Move to Recycle Bin</button>
          </div>
        )}

        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table>
            <thead><tr>
              <th style={{ width: 30 }}><input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? [] : items.map(i => i.entry_id))} /></th>
              <th>Image</th><th>Client</th><th>Work</th><th>Work date</th><th>Uploaded</th><th>Uploaded by</th><th style={{ textAlign: 'right' }}>Size</th><th>Used on</th>
            </tr></thead>
            <tbody>
              {isLoading ? <tr><td colSpan={9}><div className="empty"><div className="spinner" /></div></td></tr>
                : items.length === 0 ? <tr><td colSpan={9}><div className="empty">No images found for this filter</div></td></tr>
                : items.map(i => (
                  <tr key={i.entry_id}>
                    <td><input type="checkbox" checked={selected.includes(i.entry_id)} onChange={() => toggle(i.entry_id)} /></td>
                    <td>{i.missing
                      ? <span style={{ fontSize: 11, color: 'var(--red)' }}>file missing</span>
                      : <img src={`/uploads/proofs/${i.filename}`} alt="" onClick={() => setLightbox(`/uploads/proofs/${i.filename}`)}
                          style={{ height: 44, width: 44, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)', cursor: 'zoom-in' }} />}</td>
                    <td style={{ fontSize: 12 }}>{i.client_name}</td>
                    <td style={{ fontSize: 12, maxWidth: 320 }}>{i.description}{i.entry_deleted && <span className="pill pill-gray" style={{ marginLeft: 6, fontSize: 9 }}>entry deleted</span>}</td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtD(i.work_date)}</td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDT(i.uploaded_at)}</td>
                    <td style={{ fontSize: 12 }}>{i.uploaded_by}</td>
                    <td style={{ fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtBytes(i.size_bytes)}</td>
                    <td style={{ fontSize: 11 }}>{i.invoice_number ? <span className="pill pill-blue">{i.invoice_number}</span> : <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
          "Uploaded by" shows who attached the image; for older images, before this was tracked, it shows who logged the work. Deleting an image removes it from its Work Log entry and from the invoice editor's reference thumbnails.
        </div>
      </>}

      {tab === 'bin' && <>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Deleted images stay here for <strong>{bin?.retention_days || 7} days</strong> and are then removed permanently. {binItems.length > 0 && <>Bin size: <strong>{fmtBytes(bin?.bytes)}</strong>.</>}
        </div>
        {binItems.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <button className="btn-sm" disabled={!binSelected.length || restore.isPending} onClick={() => restore.mutate(binSelected)}>↩ Restore selected ({binSelected.length})</button>
            <button className="btn-danger btn-sm" disabled={!binSelected.length || purge.isPending}
              onClick={() => window.confirm(`Permanently delete ${binSelected.length} image(s)? This cannot be undone.`) && purge.mutate({ ids: binSelected })}>Delete selected forever</button>
            <div style={{ flex: 1 }} />
            <button className="btn-danger btn-sm" disabled={purge.isPending}
              onClick={() => window.confirm(`Empty the Recycle Bin? All ${binItems.length} image(s) (${fmtBytes(bin?.bytes)}) will be permanently deleted. This cannot be undone.`) && purge.mutate({ all: true })}>🗑 Empty Recycle Bin</button>
          </div>
        )}
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table>
            <thead><tr>
              <th style={{ width: 30 }}><input type="checkbox" checked={allBinSelected} onChange={() => setBinSelected(allBinSelected ? [] : binItems.map(i => i.id))} /></th>
              <th>Image</th><th>Client</th><th>Work</th><th>Uploaded by</th><th style={{ textAlign: 'right' }}>Size</th><th>Deleted</th><th>Time left</th><th></th>
            </tr></thead>
            <tbody>
              {binItems.length === 0 ? <tr><td colSpan={9}><div className="empty">The Recycle Bin is empty</div></td></tr>
                : binItems.map(t => (
                  <tr key={t.id}>
                    <td><input type="checkbox" checked={binSelected.includes(t.id)} onChange={() => setBinSelected(s => s.includes(t.id) ? s.filter(x => x !== t.id) : [...s, t.id])} /></td>
                    <td><img src={`/uploads/proofs_trash/${t.filename}`} alt="" onClick={() => setLightbox(`/uploads/proofs_trash/${t.filename}`)}
                      style={{ height: 44, width: 44, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)', cursor: 'zoom-in', opacity: .85 }} /></td>
                    <td style={{ fontSize: 12 }}>{t.client_name}</td>
                    <td style={{ fontSize: 12, maxWidth: 300 }}>{t.description}</td>
                    <td style={{ fontSize: 12 }}>{t.uploaded_by_name || '—'}</td>
                    <td style={{ fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtBytes(t.size_bytes)}</td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDT(t.deleted_at)}<div style={{ fontSize: 10, color: 'var(--text3)' }}>by {t.deleted_by_name || '—'}</div></td>
                    <td style={{ fontSize: 12, fontWeight: 600, color: t.days_left <= 1 ? 'var(--red)' : 'var(--text2)' }}>{t.days_left} day{t.days_left !== 1 ? 's' : ''}</td>
                    <td><button className="btn-sm" onClick={() => restore.mutate([t.id])}>↩ Restore</button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </>}

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: 24 }}>
          <img src={lightbox} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 6, background: '#fff' }} />
        </div>
      )}
    </div>
  );
}
